import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  SimpleChanges,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { loadChats } from 'src/app/store/actions/chats.actions';
import { selectChatsNewMessagesCounter } from 'src/app/store/selectors/chats.selectors';
import { Observable, Subscription, timer } from 'rxjs';
import { Domain } from 'src/app/services/domain.service';
import { selectDomain } from 'src/app/store/selectors/auth.selectors';
import { selectNewSurveysCount } from 'src/app/store/selectors/survey.selectors';
import { selectIsDisconnectedFromNetwork } from 'src/app/store/selectors/network.selectors';
import { environment } from 'src/environments/environment';

type MenuTab = 'news' | 'chats' | 'surveys' | 'user';

@Component({
  selector: 'mvp-menu',
  templateUrl: './menu.component.html',
  styleUrls: ['./menu.component.scss']
})
export class MenuComponent implements OnInit, OnDestroy, AfterViewInit, OnChanges {
  private readonly activeRouteGroups: Record<string, string[]> = {
    '/news-bis': ['/news-bis', '/news', '/article/'],
    '/chats': ['/chats', '/chat/', '/new-chat'],
    '/surveys': ['/surveys', '/sondage/'],
    '/profile': ['/profile'],
  };
  private readonly routeTabs: ReadonlyArray<{ tab: MenuTab; route: string }> = [
    { tab: 'news', route: '/news-bis' },
    { tab: 'chats', route: '/chats' },
    { tab: 'surveys', route: '/surveys' },
  ];

  currentRoute: string;
  environment = environment;
  activeIndicatorLeft = 0;
  activeIndicatorVisible = false;
  newMessages$: Observable<number>;
  newSurveysCount$: Observable<number>;
  isDisconnectedFromNetwork$: Observable<boolean>;
  domain$: Observable<Domain>;
  @Input() isMenuOpen: boolean;
  @Output() openMenu = new EventEmitter<void>();
  private chatsRefreshSub?: Subscription;
  private menuItemsChangeSub?: Subscription;
  private pendingIndicatorFrame?: number;
  @ViewChild('menuNav') private menuNav?: ElementRef<HTMLElement>;
  @ViewChildren('menuItem', { read: ElementRef }) private menuItems?: QueryList<ElementRef<HTMLElement>>;

  constructor(
    private router: Router,
    private store: Store,
  ) {
    // set currentRoute
    this.currentRoute = this.router.url;
    this.isDisconnectedFromNetwork$ = this.store.select(selectIsDisconnectedFromNetwork);
    // events on Router Update
    router.events.subscribe((link) => {
      // get current url path
      if (link instanceof NavigationEnd) {
        this.currentRoute = link.url;
        this.scheduleIndicatorUpdate();
      }
    });
    this.newMessages$ = this.store.select(selectChatsNewMessagesCounter);
    this.domain$ = this.store.select(selectDomain);
    this.newSurveysCount$ = this.store.select(selectNewSurveysCount);
  }

  ngOnInit() {
    this.store.dispatch(loadChats());

    // Socket updates are currently unreliable, keep unread state fresh.
    this.chatsRefreshSub = timer(15000, 15000).subscribe(() => {
      this.store.dispatch(loadChats());
    });
  }

  ngAfterViewInit() {
    this.menuItemsChangeSub = this.menuItems?.changes.subscribe(() => {
      this.scheduleIndicatorUpdate();
    });

    this.scheduleIndicatorUpdate();
  }

  ngOnChanges(changes: SimpleChanges) {
    if ('isMenuOpen' in changes) {
      this.scheduleIndicatorUpdate();
    }
  }

  ngOnDestroy() {
    this.chatsRefreshSub?.unsubscribe();
    this.menuItemsChangeSub?.unsubscribe();

    if (typeof window !== 'undefined' && this.pendingIndicatorFrame) {
      window.cancelAnimationFrame(this.pendingIndicatorFrame);
    }
  }

  isCurrent(url: string) {
    const currentRoute = this.currentRoute.split('?')[0];
    const routesToMatch = this.activeRouteGroups[url] ?? [url];

    return routesToMatch.some((route) =>
      route.endsWith('/')
        ? currentRoute.startsWith(route)
        : currentRoute === route || currentRoute.startsWith(`${route}/`)
    );
  }

  isUserCurrent() {
    return this.isMenuOpen || this.isCurrent('/profile');
  }

  isTabActive(tab: MenuTab): boolean {
    return this.getActiveTab() === tab;
  }

  toggleMenu() {
    this.openMenu.emit();
  }

  @HostListener('window:resize')
  onWindowResize() {
    this.scheduleIndicatorUpdate();
  }

  private scheduleIndicatorUpdate() {
    if (typeof window === 'undefined') {
      return;
    }

    if (this.pendingIndicatorFrame) {
      window.cancelAnimationFrame(this.pendingIndicatorFrame);
    }

    this.pendingIndicatorFrame = window.requestAnimationFrame(() => {
      this.pendingIndicatorFrame = undefined;
      this.updateActiveIndicator();
    });
  }

  private updateActiveIndicator() {
    const nav = this.menuNav?.nativeElement;
    const activeTab = this.getActiveTab();

    if (!nav || !activeTab) {
      this.activeIndicatorVisible = false;
      return;
    }

    const activeItem = nav.querySelector<HTMLElement>(`[data-tab="${activeTab}"]`);
    if (!activeItem) {
      this.activeIndicatorVisible = false;
      return;
    }

    this.activeIndicatorLeft = Math.round(activeItem.offsetLeft + (activeItem.offsetWidth / 2));
    this.activeIndicatorVisible = true;
  }

  private getActiveTab(): MenuTab | null {
    if (this.isMenuOpen || this.isCurrent('/profile')) {
      return 'user';
    }

    return this.routeTabs.find(({ route }) => this.isCurrent(route))?.tab ?? null;
  }
}
